import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Briefcase, Mail, CheckCircle2, AlertCircle, DollarSign, TrendingDown } from "lucide-react";

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 card-soft" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">{label}</div>
          <div className="font-display text-4xl mt-2 text-[#0F172A]">{value}</div>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: accent + "1a", color: accent }}
        >
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const now = new Date();
  const monthOptions = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const yearOptions = Array.from({ length: 7 }, (_, index) => now.getFullYear() - 6 + index);

  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [stats, setStats] = useState({
    travelers: 0,
    packages: 0,
    active_schedules: 0,
    sent_emails: 0,
    failed_emails: 0,
    company_expenses: 0,
    travel_expenses: 0,
    income: 0,
    net_income: 0,
    current_month: {
      company_expenses: 0,
      travel_expenses: 0,
      income: 0,
      net_income: 0,
    },
    previous_month: {
      company_expenses: 0,
      travel_expenses: 0,
      income: 0,
      net_income: 0,
    },
  });
  const [recent, setRecent] = useState([]);

  const currentMonth = stats.current_month || {
    company_expenses: Number(stats.company_expenses || 0),
    travel_expenses: Number(stats.travel_expenses || 0),
    income: Number(stats.income || 0),
    net_income: Number(stats.net_income || 0),
  };

  const previousMonth = stats.previous_month || {
    company_expenses: 0,
    travel_expenses: 0,
    income: 0,
    net_income: 0,
  };

  const selectedMonthLabel = monthOptions[selectedMonth - 1] || monthOptions[0];
  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  useEffect(() => {
    api.get("/stats", { params: { month: selectedMonth, year: selectedYear } })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    api.get("/logs?limit=8").then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="overline">Overview</div>
        <h1 className="font-display text-4xl mt-2 text-[#0F172A]">Welcome back</h1>
        <p className="text-[#64748B] mt-2">A snapshot of your travelers, packages, and campaigns.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
        <StatCard label="Travelers" value={stats.travelers} icon={Users} accent="#1E3A8A" testid="stat-travelers" />
        <StatCard label="Packages" value={stats.packages} icon={Briefcase} accent="#F59E0B" testid="stat-packages" />
        <StatCard label="Active Schedules" value={stats.active_schedules} icon={Mail} accent="#1E3A8A" testid="stat-schedules" />
        <StatCard label="Emails Sent" value={stats.sent_emails} icon={CheckCircle2} accent="#16A34A" testid="stat-sent" />
        <StatCard label="Failed" value={stats.failed_emails} icon={AlertCircle} accent="#DC2626" testid="stat-failed" />
      </div>

      {/* Finance Summary */}
      <div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
          <div className="overline mb-0">Finance summary</div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex flex-col">
              <label className="text-xs uppercase tracking-[0.2em] text-[#64748B] mb-1">Month</label>
              <select
                className="border border-[#E2E8F0] rounded-xl bg-white px-3 py-2 text-sm text-[#0F172A]"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {monthOptions.map((month, index) => (
                  <option key={month} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-xs uppercase tracking-[0.2em] text-[#64748B] mb-1">Year</label>
              <select
                className="border border-[#E2E8F0] rounded-xl bg-white px-3 py-2 text-sm text-[#0F172A]"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mb-4 text-sm text-[#64748B]">
          Showing {selectedMonthLabel} {selectedYear} vs previous month
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            label={`${selectedMonthLabel} ${selectedYear} • Company Expenses`}
            value={formatCurrency(currentMonth.company_expenses)}
            icon={TrendingDown}
            accent="#DC2626"
            testid="stat-company-expenses"
          />
          <StatCard
            label={`${selectedMonthLabel} ${selectedYear} • Travel Expenses`}
            value={formatCurrency(currentMonth.travel_expenses)}
            icon={DollarSign}
            accent="#F59E0B"
            testid="stat-travel-expenses"
          />
          <StatCard
            label={`${selectedMonthLabel} ${selectedYear} • Income`}
            value={formatCurrency(currentMonth.income)}
            icon={DollarSign}
            accent="#16A34A"
            testid="stat-income"
          />
          <StatCard
            label={`${selectedMonthLabel} ${selectedYear} • Net Income`}
            value={formatCurrency(currentMonth.net_income)}
            icon={CheckCircle2}
            accent="#1E40AF"
            testid="stat-net-income"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-5">
          <StatCard
            label={`Previous month • Company Expenses`}
            value={formatCurrency(previousMonth.company_expenses)}
            icon={TrendingDown}
            accent="#7C2D12"
            testid="stat-company-expenses-prev"
          />
          <StatCard
            label={`Previous month • Travel Expenses`}
            value={formatCurrency(previousMonth.travel_expenses)}
            icon={DollarSign}
            accent="#B45309"
            testid="stat-travel-expenses-prev"
          />
          <StatCard
            label={`Previous month • Income`}
            value={formatCurrency(previousMonth.income)}
            icon={DollarSign}
            accent="#15803D"
            testid="stat-income-prev"
          />
          <StatCard
            label={`Previous month • Net Income`}
            value={formatCurrency(previousMonth.net_income)}
            icon={CheckCircle2}
            accent="#1D4ED8"
            testid="stat-net-income-prev"
          />
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl card-soft">
        <div className="p-6 border-b border-[#E2E8F0]">
          <div className="overline">Activity</div>
          <h2 className="font-display text-2xl mt-1 text-[#0F172A]">Recent emails</h2>
        </div>
        <div className="divide-y divide-[#E2E8F0]">
          {recent.length === 0 ? (
            <div className="p-10 text-center text-[#64748B] text-sm">No emails sent yet. Create a schedule to begin.</div>
          ) : (
            recent.map((log) => (
              <div key={log.id} className="flex items-center justify-between px-6 py-4">
                <div>
                  <div className="text-sm font-medium text-[#0F172A]">{log.subject}</div>
                  <div className="text-xs text-[#64748B] mt-1">
                    To {log.traveler_email} · {log.package_title}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {log.mocked && <span className="text-[10px] uppercase tracking-widest text-[#F59E0B]">Mocked</span>}
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full ${
                      log.status === "sent" ? "bg-[#16A34A]/10 text-[#16A34A]" : "bg-[#DC2626]/10 text-[#DC2626]"
                    }`}
                  >
                    {log.status}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
